import { useQuery } from '@tanstack/react-query';
import React, { FC, useMemo, useRef, useState } from 'react';

import { type BankAccountWithDetails, bankAccountGetBankAccount } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Popover, PopoverContent, PopoverTrigger, formatAmount } from '@column/ui';

import { useBankAccountOptions } from '~/components/fields/BankAccountDropdown';
import { bankAccountDropdownGetQueryKey } from '~/lib/api/bankAccounts/bankAccountDropdownQueryKey';
import { type Action } from '~/lib/authz/eval';
import { type HighlightSuffixCandidate, getHighlightedSuffix } from '~/lib/search/highlight';
import { useSessionStore } from '~/stores/Session';

import {
  FADE_ONLY_MOTION,
  FadeSwap,
  OVERLAP_CELL,
  OVERLAP_GRID,
  POPOVER_CONTENT_CLASS,
  SelectedCard,
  type SelectedCardProps,
  type SelectedCardRow,
  type SelectorOption,
  SelectorSearch,
  SelectorTrigger,
  TRIGGER_HEIGHT_PX,
  highlightMatch,
  renderMatchFragment,
} from './primitives';

// Fields not otherwise shown in a row; when the search matched one, it's revealed in place of the account number.
const HIDDEN_MATCH_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'ACCOUNT_NUMBERS.ROUTING_NUMBER', label: 'RTN' },
  { field: 'ACCOUNT_NUMBERS.DESCRIPTION', label: 'ACCT DESC' },
  { field: 'ACCOUNT_NUMBERS.ACCOUNT_NUMBER_ID', label: 'ACCT ID' },
  { field: 'BANK_ACCOUNT_ID', label: 'ID' },
];

export interface BankAccountSelectorProps {
  value: string;
  onChange: (id: string) => void;
  onBlur: () => void;
  permission?: Action['permission'];
  unauthorizedText?: string;
  hasError?: boolean;
  /** In minor units; flags insufficient funds when this account is the source. */
  amount?: number;
  /** Currency of `amount`; when set, the balance check is skipped for accounts in other currencies. */
  amountCurrency?: string;
  isSource?: boolean;
  /** Overrides the insufficient-funds alert; receives the account's available balance in minor units. */
  insufficientMessage?: (availableAmount: number, currency: string) => string;
  insufficientTone?: SelectedCardProps['alertTone'];
  dataCy?: string;
}

export const BankAccountSelector: FC<BankAccountSelectorProps> = ({
  value,
  onChange,
  onBlur,
  permission,
  unauthorizedText,
  hasError,
  amount,
  amountCurrency,
  isSource,
  insufficientMessage,
  insufficientTone,
  dataCy,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedOnClose = useRef(false);
  const { isAuthorized } = useSessionStore();
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const { bankAccounts, search, handleScrollEnd } = useBankAccountOptions({ value });

  const activeQuery = useQuery({
    queryKey: bankAccountDropdownGetQueryKey(platformId, isSandbox, value || undefined),
    queryFn: async () => {
      const { data } = await bankAccountGetBankAccount({ path: { bank_account_id: value }, throwOnError: true });
      return data;
    },
    enabled: Boolean(value),
    placeholderData: () => bankAccounts.find((ba) => ba.id === value),
    staleTime: 5 * 60_000,
    // A 403 here is deterministic (per-account authorization), so don't retry.
    retry: false,
  });
  // Fall back to the list entry so a failed or forbidden detail fetch never blanks the card.
  const account = (activeQuery.data ?? bankAccounts.find((ba) => ba.id === value)) as
    | BankAccountWithDetails
    | undefined;

  const options: SelectorOption[] = useMemo(
    () =>
      bankAccounts.map((ba) => {
        const authorized = !permission
          ? true
          : isAuthorized({ permission, permissionLevel: 'write', bankAccount: { id: ba.id, owners: ba.owners } });
        const matchedHidden = getHighlightedSuffix(ba.search_metadata, HIDDEN_MATCH_CANDIDATES);
        return {
          id: ba.id,
          primary: highlightMatch(ba.display_name || ba.description || 'Unnamed', query),
          secondary: matchedHidden ? (
            <span className="font-mono">
              <span className="text-muted-foreground/60">{matchedHidden.label} </span>
              {renderMatchFragment(matchedHidden.fragment)}
            </span>
          ) : ba.default_account_number ? (
            <span className="font-mono">
              <span className="text-muted-foreground/60">ACCT </span>
              {highlightMatch(ba.default_account_number, query)}
            </span>
          ) : undefined,
          trailingPrimary: formatAmount(ba.balances?.available_amount ?? 0, ba.currency_code),
          trailingSecondary: 'Available',
          disabled: !authorized,
          adornment:
            !authorized && unauthorizedText ? (
              <span title={unauthorizedText} className="flex text-muted-foreground">
                <Icon.AnimationLock className="[--icon-size-x:16px] [--icon-size-y:16px]" />
              </span>
            ) : undefined,
        };
      }),
    [bankAccounts, isAuthorized, permission, unauthorizedText, query]
  );

  const availableAmount = account?.balances?.available_amount;
  const insufficient =
    Boolean(isSource) &&
    typeof amount === 'number' &&
    amount > 0 &&
    typeof availableAmount === 'number' &&
    (!amountCurrency || account?.currency_code === amountCurrency) &&
    amount > availableAmount;

  const rows: SelectedCardRow[] = account
    ? [
        {
          label: 'Available balance',
          value: formatAmount(availableAmount ?? 0, account.currency_code),
          emphasized: true,
        },
        { label: 'Account number', value: account.default_account_number || '—', monospaced: true },
        { label: 'Routing number', value: account.routing_number || '—', monospaced: true },
      ]
    : [];

  return (
    <div className={OVERLAP_GRID} data-cy={dataCy}>
      <SelectedCard
        className={OVERLAP_CELL}
        open={Boolean(value)}
        title={account?.display_name || account?.description || 'Bank account'}
        subtitle={value ? `ID: ${value}` : undefined}
        icon={<Icon.Wallet className="[--icon-size-x:20px] [--icon-size-y:20px]" />}
        rows={rows}
        isLoading={!account && activeQuery.isLoading}
        hasError={hasError}
        alert={
          insufficient
            ? (insufficientMessage?.(availableAmount ?? 0, account?.currency_code ?? '') ??
              'Insufficient balance to create this transfer')
            : undefined
        }
        alertTone={insufficientTone}
        onClear={() => onChange('')}
      />
      <FadeSwap visible={!value} className={OVERLAP_CELL}>
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) {
              onBlur();
              setQuery('');
              search('');
            }
          }}
        >
          <PopoverTrigger asChild>
            <SelectorTrigger label="Select a bank account" hasError={hasError} />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={-TRIGGER_HEIGHT_PX}
            className={POPOVER_CONTENT_CLASS}
            {...FADE_ONLY_MOTION}
            onCloseAutoFocus={(event) => {
              // After a selection the trigger is hidden, so don't restore focus to it.
              if (selectedOnClose.current) {
                selectedOnClose.current = false;
                event.preventDefault();
              }
            }}
          >
            <SelectorSearch
              options={options}
              searchPlaceholder="Search bank accounts…"
              emptyMessage="No accounts found"
              onSearch={(q) => {
                setQuery(q);
                search(q);
              }}
              onSelect={(id) => {
                selectedOnClose.current = true;
                onChange(id);
                setOpen(false);
                setQuery('');
                search('');
              }}
              onScrollEnd={handleScrollEnd}
            />
          </PopoverContent>
        </Popover>
      </FadeSwap>
    </div>
  );
};
